import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { LineChart, Line, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend, ReferenceArea } from 'recharts';
import { useColorMode } from '../../../../theme/ColorModeProvider';
import { getChartPalette } from '../../../../theme/colorMode';
import { Section, Panel, MetricGrid, DataTable, Callout, HiddenChartTable } from './shared';
import LatestSignIns from './LatestSignIns';
import { formatCount, formatDay, formatDecimal } from '../utils';
import { useInsightsStyles } from '../styles';

/** `logins` section. */
const DailyLogins = ({ data = {}, service }) => {
  const classes = useInsightsStyles();
  const chart = getChartPalette(useColorMode().mode);
  const testNote = data.test_accounts?.note;
  const latest = <LatestSignIns initial={data.latest || []} service={service} />;

  if (!data.available) {
    return (
      <Section id="logins" title="Daily logins" subtitle={data.source ? `Source: ${data.source}` : undefined} exportKey="logins">
        <Panel>
          <Callout title={data.reason || data.description} />
          {(data.how_to_enable || []).length ? (
            <Accordion elevation={0} style={{ border: '1px solid var(--dm-border, #e7ebf0)', borderRadius: 12, background: 'transparent' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography style={{ fontWeight: 700, fontSize: '.84rem' }}>How to enable</Typography></AccordionSummary>
              <AccordionDetails>
                <ol style={{ margin: 0, paddingLeft: 20 }}>{data.how_to_enable.map((step, index) => <li key={index}><Typography className={classes.description}>{step}</Typography></li>)}</ol>
              </AccordionDetails>
            </Accordion>
          ) : null}
          {testNote ? <Typography className={classes.footnote}>{testNote}</Typography> : null}
          {latest}
        </Panel>
      </Section>
    );
  }

  const points = (data.days || []).map((day, index) => ({
    day,
    label: formatDay(day),
    logins: data.logins?.[index] ?? 0,
    users: data.unique_users?.[index] ?? 0,
    failed: data.failed_logins?.[index] ?? 0,
  }));
  const overview = [
    { key: 'avg-users', label: 'People signing in per day (average)', value: data.avg_unique_users_per_day },
    { key: 'avg-logins', label: 'Sign-ins per day (average)', value: data.avg_logins_per_day },
    { key: 'unique', label: 'Different people in the window', value: data.unique_users_in_period },
    ...(data.online_now === null || data.online_now === undefined ? [] : [{ key: 'online', label: 'Online now', value: data.online_now }]),
  ];
  const trackingStartsInside = data.tracking_since && points.length && new Date(data.tracking_since) > new Date(points[0].day);

  return (
    <Section id="logins" title="Daily logins" subtitle={data.description} exportKey="logins">
      <Panel>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          {overview.map(item => (
            <div key={item.key} className={classes.tile}>
              <Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>{item.label}</Typography>
              <Typography className={classes.metricValue}>{formatDecimal(item.value)}</Typography>
            </div>
          ))}
        </div>
        {testNote ? <Typography className={classes.footnote} data-testid="login-test-note">{testNote}</Typography> : null}
        {data.coverage_note ? <div style={{ marginTop: 16 }}><Callout info body={data.coverage_note} /></div> : null}
        <div className={classes.chart} role="img" aria-label="Daily sign-ins, different people, and failed password sign-ins line chart">
          <ResponsiveContainer><LineChart data={points} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}><CartesianGrid stroke={chart.grid} vertical={false} /><XAxis dataKey="label" stroke={chart.axis} tick={{ fontSize: 12 }} minTickGap={16} /><YAxis stroke={chart.axis} tick={{ fontSize: 12 }} allowDecimals={false} /><Tooltip contentStyle={{ background: chart.tooltipBg, borderColor: chart.tooltipBorder }} formatter={value => formatCount(value)} /><Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {trackingStartsInside ? <ReferenceArea x1={points[0].label} x2={formatDay(data.tracking_since)} fill="#94a3b8" fillOpacity={0.16} /> : null}
            <Line type="monotone" dataKey="logins" name="Sign-ins" stroke="#3D7DDD" strokeWidth={2} dot={false} /><Line type="monotone" dataKey="users" name="Different people" stroke="#16a34a" strokeWidth={2} dot={false} /><Line type="monotone" dataKey="failed" name="Failed password sign-ins" stroke="#dc2626" strokeWidth={2} dot={false} />
          </LineChart></ResponsiveContainer>
        </div>
        <HiddenChartTable caption="Daily sign-in activity" headers={['Day', 'Sign-ins', 'Different people', 'Failed password sign-ins']} rows={points.map(item => [item.label, item.logins, item.users, item.failed])} />
        {data.peak_day ? <Typography className={classes.footnote}>Busiest day: {formatDay(data.peak_day)} with {formatCount(data.peak_unique_users)} people.</Typography> : null}
        {data.source ? <Typography className={classes.footnote}>Source: {data.source}</Typography> : null}
        {data.truncated ? <Typography className={classes.footnote}>Only the newest login events were read.</Typography> : null}
        <div style={{ marginTop: 16 }}><MetricGrid items={data.active_learners || []} /></div>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12} md={6} lg={4}><Typography component="h3" className={classes.subheading} style={{ marginTop: 0 }}>Sign-ins by role</Typography><DataTable compact ariaLabel="Sign-ins by role" columns={[{ key: 'label', label: 'Role' }, { key: 'users', label: 'People', align: 'right', render: row => formatCount(row.users) }, { key: 'logins', label: 'Sign-ins', align: 'right', render: row => formatCount(row.logins) }]} rows={data.by_role || []} getRowKey={row => row.role} /></Grid>
          <Grid item xs={12} md={6} lg={4}><Typography component="h3" className={classes.subheading} style={{ marginTop: 0 }}>How people sign in</Typography><DataTable compact ariaLabel="Sign-in methods" columns={[{ key: 'label', label: 'Method' }, { key: 'logins', label: 'Sign-ins', align: 'right', render: row => formatCount(row.logins) }]} rows={data.by_method || []} /></Grid>
          <Grid item xs={12} lg={4}><Typography component="h3" className={classes.subheading} style={{ marginTop: 0 }}>Companies with the most active learners</Typography><DataTable compact ariaLabel="Companies with the most active learners" columns={[{ key: 'customer', label: 'Company' }, { key: 'active_learners', label: 'Active learners', align: 'right', render: row => formatCount(row.active_learners) }]} rows={data.top_companies || []} getRowKey={(row, index) => `${row.customer}-${index}`} /></Grid>
        </Grid>
        {latest}
      </Panel>
    </Section>
  );
};

export default DailyLogins;
